import { useEffect, useMemo, useRef, useState } from 'react'
import {
  SearchTemplateKeywordsCombobox,
  SelectTemplateCategoryCombobox,
} from '@components/app/panels/right-panel/combo-box'
import {
  TRI_STATE_FILTER_OPTIONS,
  TrayEmptyState,
  TrayFilterSegmentRow,
  TrayGrid,
  TrayLoadingSkeleton,
  TrayPanelLayout,
  TraySearchInput,
  useAnyDropdownOpen,
  useControllableValue,
} from '@components/app/panels/right-panel/tray'
import { TemplateThumbnail } from '@/components/app/templates/template-thumbnail'
import { useTemplatePlacementOptional } from '@/contexts/template-placement-context'
import { useTemplateListQuery } from '@/api/hooks/use-templates'
import { useTrayLazyWindow } from '@/hooks/use-tray-lazy-window'
import { filterTemplatesByKeywords } from '@/lib/templates/filter-templates-by-keywords'
import { TRAY_SINGLE_COLUMN_BOX_CLASS } from '@/lib/tray/tray-thumbnail-box'
import { filterTemplatesByName } from '@/lib/templates/filter-templates-by-name'
import {
  DEFAULT_TEMPLATE_CATEGORY_VALUE,
  type TemplateCategorySelection,
  decodeTemplateCategorySelection,
  encodeTemplateCategorySelection,
} from '@/lib/templates/template-category-selection'
import {
  TEMPLATES_SORT_DEFAULT,
  TEMPLATES_SORT_OPTIONS,
  type TemplatesSort,
  sortTemplates,
} from '@/lib/templates/sort-templates'
import { TEMPLATES_SINGLE_COLUMN_MAX } from './constants/templates-single-column-max'

const DEFAULT_SELECTION: TemplateCategorySelection = { type: 'curated', key: 'custom' }

/** UI-only for now — favoriting isn't in the template API yet (confirmed with product,
 *  2026-09-29); no field exists on `LibraryTemplate`/`CustomerTemplate` to filter by, so this
 *  toggle doesn't affect the gallery yet. Same treatment as Backgrounds' own "Placed" filter
 *  while its API param was still pending. Still counts toward the Filters badge — it's a real
 *  applied filter from the user's perspective even though it's a no-op today. */
export type TemplatesFavoritedFilter = 'any' | 'yes' | 'no'

type TemplatesPanelProps = {
  /** Reports "is one of my dropdowns (Category / Keywords / Sort) open" so a host panel can drive
   *  an Escape-key priority chain without owning any of this panel's internal state — mirrors
   *  BackgroundsPanel's `onAnyDropdownOpenChange`. */
  onAnyDropdownOpenChange?: (open: boolean) => void
  /** Controlled category selection and applied filters (keyword ids, name search, favorited) —
   *  let a host (`right-panel/root-panel/hooks/use-templates-filters.ts`) persist all four
   *  across this panel's own unmount when the right panel collapses or switches to a different rail icon (WMPCM-11026 AC-8). Each falls
   *  back to internal state when omitted, so the component still works standalone (e.g. in
   *  tests). Sort and "Show filename" are NOT lifted — out of scope for this AC, matching
   *  ClikArt's own precedent of only persisting category/search. */
  categoryValue?: string
  onCategoryValueChange?: (value: string) => void
  selectedKeywordIds?: string[]
  onSelectedKeywordIdsChange?: (ids: string[]) => void
  nameQuery?: string
  onNameQueryChange?: (query: string) => void
  favoritedFilter?: TemplatesFavoritedFilter
  onFavoritedFilterChange?: (filter: TemplatesFavoritedFilter) => void
}

export function TemplatesPanel({
  onAnyDropdownOpenChange,
  categoryValue: categoryValueProp,
  onCategoryValueChange,
  selectedKeywordIds: selectedKeywordIdsProp,
  onSelectedKeywordIdsChange,
  nameQuery: nameQueryProp,
  onNameQueryChange,
  favoritedFilter: favoritedFilterProp,
  onFavoritedFilterChange,
}: TemplatesPanelProps = {}) {
  const scrollRootRef = useRef<HTMLDivElement>(null)

  // A "place over existing content?" prompt belongs to this panel's tray: closing the panel, or
  // switching to another rail icon, unmounts it and takes the prompt with it. `cancelPendingPlacement`
  // is stable, so this runs only on unmount.
  const cancelPendingPlacement = useTemplatePlacementOptional()?.cancelPendingPlacement

  useEffect(() => cancelPendingPlacement, [cancelPendingPlacement])

  const [categoryValue, setCategoryValue] = useControllableValue(
    categoryValueProp,
    onCategoryValueChange,
    DEFAULT_TEMPLATE_CATEGORY_VALUE
  )
  const [selectedKeywordIds, setSelectedKeywordIds] = useControllableValue<string[]>(
    selectedKeywordIdsProp,
    onSelectedKeywordIdsChange,
    []
  )
  const [nameQuery, setNameQuery] = useControllableValue(nameQueryProp, onNameQueryChange, '')
  const [favoritedFilter, setFavoritedFilter] = useControllableValue<TemplatesFavoritedFilter>(
    favoritedFilterProp,
    onFavoritedFilterChange,
    'any'
  )

  const [showTemplateName, setShowTemplateName] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [sort, setSort] = useState<TemplatesSort>(TEMPLATES_SORT_DEFAULT)

  const reportAnyDropdownOpenChange = useAnyDropdownOpen(
    { category: false, keywords: false, sort: false, preview: false },
    onAnyDropdownOpenChange
  )

  // Memoized: every thumbnail's effect depends on this identity, so a fresh function per render
  // would re-report "closed" while a preview is still open (same as `PhotosPanel`).
  const reportPreviewOpenChange = useMemo(
    () => reportAnyDropdownOpenChange('preview'),
    [reportAnyDropdownOpenChange]
  )

  const selection = decodeTemplateCategorySelection(categoryValue) ?? DEFAULT_SELECTION

  const { data: allTemplates = [], isLoading, isError } = useTemplateListQuery(selection)

  // Neither endpoint takes a keyword or name filter param (see `templates.service.ts`) — both
  // happen client-side over whatever the selected category/all/custom source returned. A
  // customer template is never keyword-tagged, so selecting any keyword correctly excludes every
  // Custom Templates result.
  const templates = useMemo(
    () =>
      sortTemplates(
        filterTemplatesByName(
          filterTemplatesByKeywords(allTemplates, selectedKeywordIds),
          nameQuery
        ),
        sort
      ),
    [allTemplates, selectedKeywordIds, nameQuery, sort]
  )

  const {
    visibleItems: visibleTemplates,
    hasMore,
    // 1 column up to `TEMPLATES_SINGLE_COLUMN_MAX` results, 2 beyond (not the tray default of
    // 10). Counted over the filtered results, not the lazy window, so a filter change that
    // crosses the threshold switches the layout immediately.
    columns: columnCount,
    sentinelRef,
  } = useTrayLazyWindow({
    items: templates,
    scrollRootRef,
    resetKey: JSON.stringify([categoryValue, selectedKeywordIds, nameQuery, sort]),
    singleColumnMax: TEMPLATES_SINGLE_COLUMN_MAX,
  })

  // Category is a separate control outside the Filters section (matches Photos' album and
  // Backgrounds'/ClikArt's own Type/Category — none of them count toward this badge or get
  // touched by Clear filters either).
  const activeFilterCount =
    selectedKeywordIds.length +
    (nameQuery.trim() !== '' ? 1 : 0) +
    (favoritedFilter !== 'any' ? 1 : 0)

  // Plain functions, not `useCallback` — `setSelectedKeywordIds`/`setNameQuery`/`setCategoryValue`
  // above are themselves recreated every render (they close over the current prop callbacks), so
  // memoizing these with an empty deps array would capture stale closures.
  function handleClearFilters() {
    setSelectedKeywordIds([])
    setNameQuery('')
    setFavoritedFilter('any')
  }

  function handleCategoryChange(next: TemplateCategorySelection) {
    setCategoryValue(encodeTemplateCategorySelection(next))
  }

  const resultCount = templates.length
  const showResultCount = !isLoading && !isError

  return (
    <TrayPanelLayout
      scrollRootRef={scrollRootRef}
      fixedHeader
      picker={
        <SelectTemplateCategoryCombobox
          value={selection}
          onChange={handleCategoryChange}
          onOpenChange={reportAnyDropdownOpenChange('category')}
        />
      }
      filtersOpen={filtersOpen}
      onFiltersOpenChange={setFiltersOpen}
      activeFilterCount={activeFilterCount}
      onClearFilters={handleClearFilters}
      filters={
        <>
          <div className="flex flex-col gap-2 px-4 pb-2 pt-2">
            <SearchTemplateKeywordsCombobox
              value={selectedKeywordIds}
              onChange={setSelectedKeywordIds}
              onOpenChange={reportAnyDropdownOpenChange('keywords')}
            />
          </div>
          <div className="px-4 pb-4 pt-2">
            <TraySearchInput
              value={nameQuery}
              onChange={setNameQuery}
              placeholder="Search by filename"
            />
          </div>
          <div className="flex flex-col gap-3">
            <TrayFilterSegmentRow
              label="Favorited"
              value={favoritedFilter}
              options={TRI_STATE_FILTER_OPTIONS}
              onChange={setFavoritedFilter}
              className="px-4"
            />
          </div>
        </>
      }
      showFilename={showTemplateName}
      onShowFilenameChange={setShowTemplateName}
      resultCount={resultCount}
      showResultCount={showResultCount}
      sort={{
        value: sort,
        options: TEMPLATES_SORT_OPTIONS,
        onValueChange: setSort,
        ariaLabel: 'Sort templates',
        listboxLabel: 'Sort templates by',
        onOpenChange: reportAnyDropdownOpenChange('sort'),
      }}
    >
      {isLoading ? (
        // Known limitation (WMPCM-12017 AC 3): the layout is chosen from the result count
        // (`TEMPLATES_SINGLE_COLUMN_MAX`), which isn't known until the list arrives, and no API
        // returns a category's size ahead of it. So this matches the one-column gallery exactly,
        // and a category over the threshold still reflows to two columns once it loads. Each
        // card keeps its own image skeleton from there. Needs a product decision (or a count
        // from the backend) to remove.
        <TrayLoadingSkeleton
          label="Loading templates"
          columns={1}
          framed
          // `TrayThumbnailCard`'s shell (its radius, background and single-column width cap), so
          // the real cards swap in without a layout shift.
          frameClassName="rounded-md bg-background max-w-[var(--panel-single-photo-width)]"
          itemClassName={TRAY_SINGLE_COLUMN_BOX_CLASS}
        />
      ) : isError ? (
        <p className="px-1 py-4 text-sm text-destructive">Unable to load templates.</p>
      ) : resultCount === 0 ? (
        <TrayEmptyState title="No templates found" description="Try adjusting your filters" />
      ) : (
        <TrayGrid columns={columnCount} sentinelRef={sentinelRef} showSentinel={hasMore}>
          {visibleTemplates.map((template) => (
            <TemplateThumbnail
              key={template.id}
              template={template}
              showTemplateName={showTemplateName}
              columns={columnCount}
              onPreviewOpenChange={reportPreviewOpenChange}
            />
          ))}
        </TrayGrid>
      )}
    </TrayPanelLayout>
  )
}
